import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { onboardingService } from '../services/onboardingService';
import { DynamicForm } from '../form-builder';
import type {
  ExistingSubmissionQuery,
  FormSubmitValues,
} from '../form-builder';
import type { WorkflowSummary } from '../types/workflow';
import { resolveBackToListUrl } from '../utils/backToList';

/**
 * Historical fallback form name for the ONB init resource. Only used
 * when the create-metadata response is unavailable AND the caller
 * didn't specify a workflow — under multi-workflow support the
 * server chooses the resource per `initResource` in the registry.
 */
const CANDIDATE_ONBOARDING_INIT_FORM = 'candidate_onboarding_init';

/**
 * Read the `workflow_abv` search param off the URL. Trimmed, upper-
 * cased, and folded to `undefined` when empty so downstream code can
 * treat the "let the server default" case identically whether the
 * param was missing or blank.
 */
function readWorkflowAbvFromUrl(searchParams: URLSearchParams): string | undefined {
  const raw = (searchParams.get('workflow_abv') || '').trim();
  return raw ? raw.toUpperCase() : undefined;
}

export const NewCasePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [searchParams] = useSearchParams();
  const [serverError, setServerError] = useState<string | null>(null);

  const workflowAbv = useMemo(() => readWorkflowAbvFromUrl(searchParams), [searchParams]);

  /**
   * Return URL for Cancel + post-create navigation. See the
   * matching comment in `CandidateDetailPage` — same rationale
   * (preserve reviewer's filter context) and the same guarded
   * helper.
   */
  const backToList = useMemo(() => resolveBackToListUrl(location.state), [location.state]);

  // Workflow registry — used to (a) resolve the display label for the
  // page header and (b) surface the `requires_existing_candidate`
  // guard when applicable. Failures are non-fatal: we fall back to
  // the raw abbreviation for the header and skip the candidate guard
  // (server-side validation will still reject a missing candidate).
  const workflowsQuery = useQuery({
    queryKey: ['onboarding-workflows'],
    queryFn: () => onboardingService.getWorkflows(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const selectedWorkflow: WorkflowSummary | undefined = useMemo(() => {
    if (!workflowAbv) return undefined;
    return workflowsQuery.data?.find(
      (w) => w.abbreviation?.toUpperCase() === workflowAbv,
    );
  }, [workflowAbv, workflowsQuery.data]);

  const schemaQuery = useQuery({
    queryKey: ['onboarding-create-form', workflowAbv ?? '__default__'],
    queryFn: () => onboardingService.getCreateFormMetadata(workflowAbv),
    staleTime: 5 * 60 * 1000,
  });

  const createMutation = useMutation({
    mutationFn: (payload: FormSubmitValues) =>
      onboardingService.createCase(payload, workflowAbv),
    onSuccess: (response) => {
      showSuccess('Candidate Added', response.message || 'Onboarding candidate added successfully.');
      queryClient.invalidateQueries({ queryKey: ['onboarding-cases'] });
      navigate(backToList);
    },
    onError: (error: any) => {
      const message = error?.message || 'Failed to add onboarding candidate.';
      setServerError(message);
      showError('Add Candidate Failed', message);
    },
  });

  const handleSubmit = (payload: FormSubmitValues) => {
    setServerError(null);
    createMutation.mutate(payload);
  };

  const handleCancel = () => {
    navigate(backToList);
  };

  // No `uploadFile` wiring on this page — versioned-document uploads
  // target a specific case_id (Document-Upload.md §2 entity types),
  // which doesn't exist until the case is created. If the init form
  // grows a picker field, that field renders disabled until we add
  // deferred upload (POST-after-create) handling.
  const formName = schemaQuery.data?.name ?? CANDIDATE_ONBOARDING_INIT_FORM;
  const existingSubmissionFetcher = useCallback(
    (query: ExistingSubmissionQuery) => onboardingService.getExistingSubmission(formName, query),
    [formName],
  );

  // Display label for the page header. Falls back to the raw
  // abbreviation, then to the generic wording so we always show
  // something meaningful even when the registry hasn't loaded.
  const headerTitle = selectedWorkflow?.label
    ? `Add ${selectedWorkflow.label}`
    : workflowAbv
      ? `Add ${workflowAbv} case`
      : 'Add Candidate';

  // CR-style hint: the registry marks flows that require an existing
  // candidate, and the create form should include a picker field for
  // one. We render an informational banner so the reviewer knows why
  // that field is required even before they touch it.
  const requiresExistingCandidate = selectedWorkflow?.requires_existing_candidate === true;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleCancel}
          >
            Back to Candidates
          </Button>
        </div>
      </div>

      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-rgf-gray-900">{headerTitle}</h2>
        {workflowAbv && (
          <p className="text-xs text-rgf-gray-500">
            Workflow · <code className="font-mono">{workflowAbv}</code>
          </p>
        )}
      </div>

      {requiresExistingCandidate && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-700" />
          <div className="text-amber-900">
            This workflow needs an existing candidate. Pick or search the candidate on
            the form — the server will refuse a submit that doesn't reference a valid
            <code className="mx-1 font-mono">candidate_id</code> (or equivalent) for
            this workflow.
          </div>
        </div>
      )}

      {schemaQuery.isLoading ? (
        <div className="card">
          <div className="card-body text-center text-rgf-gray-500 py-12">Loading form…</div>
        </div>
      ) : schemaQuery.error ? (
        <div className="card">
          <div className="card-body text-center py-12 space-y-4">
            <p className="text-rgf-gray-700">
              {(schemaQuery.error as Error)?.message || 'Failed to load the onboarding form.'}
            </p>
            <Button onClick={() => schemaQuery.refetch()}>Try Again</Button>
          </div>
        </div>
      ) : schemaQuery.data ? (
        <DynamicForm
          schema={schemaQuery.data}
          submitting={createMutation.isPending}
          submitLabel={selectedWorkflow?.label ? `Add ${selectedWorkflow.label}` : 'Add Candidate'}
          serverError={serverError}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          existingSubmissionFetcher={existingSubmissionFetcher}
        />
      ) : null}
    </div>
  );
};
